import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MapPinned } from 'lucide-react';
import type { PlaceInput as Place } from '@tcrd/shared';
import { Button, Card, Money } from '@tcrd/ui';
import { PlaceInput } from '../../components/PlaceInput';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { fmtDuration, fmtKm } from '../../lib/format';
import type { RideDto } from '../../lib/types';

const CHANGEABLE = ['CONFIRMED', 'DRIVER_ON_THE_WAY', 'DRIVER_ARRIVED', 'IN_PROGRESS'];

interface Estimate {
  fare: string;
  currentFare: string;
  distanceMeters: number;
  durationSeconds: number;
}

/** Cliente: cambiar el destino del viaje. Ve el nuevo precio antes de enviarlo; el conductor lo acepta. */
export function DestinationChangeCustomer({ ride }: { ride: RideDto }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<Place | null>(null);
  const [est, setEst] = useState<Estimate | null>(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['ride', ride.id] });
  const body = (p: Place) => ({ address: p.address, lat: p.lat, lng: p.lng });

  if (!CHANGEABLE.includes(ride.status)) return null;
  const pending = ride.destinationChange;
  if (pending) {
    return (
      <Card className="p-4 border-2 border-amber-400/70 text-sm flex flex-col gap-2">
        <p>
          Esperando que tu conductor acepte el cambio a <strong>{pending.address}</strong> por <Money value={pending.fare} className="font-semibold" />.
        </p>
        <Button size="sm" variant="ghost" onClick={() => void api(`/rides/${ride.id}/destination`, { method: 'DELETE' }).then(refresh).catch((e) => toast(errorMessage(e), 'danger'))}>
          Cancelar el cambio
        </Button>
      </Card>
    );
  }
  if (!open) {
    return (
      <Button variant="secondary" block icon={<MapPinned className="size-5" />} onClick={() => setOpen(true)}>
        Cambiar destino
      </Button>
    );
  }

  const choose = async (p: Place | null) => {
    setPlace(p);
    setEst(null);
    if (!p) return;
    setBusy(true);
    try {
      setEst(await api<Estimate>(`/rides/${ride.id}/destination/estimate`, { body: body(p) }));
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(false);
    }
  };
  const send = async () => {
    if (!place) return;
    setBusy(true);
    try {
      await api(`/rides/${ride.id}/destination`, { body: body(place) });
      toast('Enviamos el cambio a tu conductor', 'success');
      setOpen(false);
      setPlace(null);
      setEst(null);
      await refresh();
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4 flex flex-col gap-3">
      <PlaceInput label="Nuevo destino" value={place} onChange={(p) => void choose(p)} dotClass="bg-brand-red" allowCurrentLocation={false} />
      {est && (
        <div className="rounded-2xl bg-[var(--surface-2)] p-3 text-sm">
          <p>
            Precio: <Money value={est.currentFare} className="line-through text-[var(--text-muted)]" /> → <Money value={est.fare} className="font-bold text-base" />
          </p>
          <p className="text-xs text-[var(--text-muted)]">Recorrido total {fmtKm(est.distanceMeters)} · {fmtDuration(est.durationSeconds)}. El conductor debe aceptar el cambio.</p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Button variant="secondary" onClick={() => (setOpen(false), setPlace(null), setEst(null))}>Cancelar</Button>
        <Button variant="accent" loading={busy} disabled={!est} onClick={() => void send()}>Enviar</Button>
      </div>
    </Card>
  );
}

/** Conductor: aceptar o rechazar el cambio de destino que pidió el cliente. */
export function DestinationChangeDriver({ ride, onDone }: { ride: RideDto; onDone?: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const c = ride.destinationChange;
  if (!c) return null;
  const respond = async (accept: boolean) => {
    setBusy(true);
    try {
      await api(`/rides/${ride.id}/destination/respond`, { body: { accept } });
      toast(accept ? 'Destino actualizado' : 'Cambio rechazado', accept ? 'success' : 'dark');
      await qc.invalidateQueries({ queryKey: ['ride', ride.id] });
      onDone?.();
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="p-4 border-2 border-amber-400/70 text-sm flex flex-col gap-2">
      <p className="font-semibold">El cliente quiere cambiar el destino</p>
      <p>
        {ride.destination.address} → <strong>{c.address}</strong>
      </p>
      <p>
        Precio: <Money value={ride.agreedFare} className="line-through text-[var(--text-muted)]" /> → <Money value={c.fare} className="font-bold" /> · {fmtKm(c.distanceMeters)} en total
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => void respond(false)}>Rechazar</Button>
        <Button size="sm" variant="accent" loading={busy} onClick={() => void respond(true)}>Aceptar</Button>
      </div>
    </Card>
  );
}
